import {
  Box,
  HStack,
  VStack,
  Text,
  Progress,
  Badge,
  SimpleGrid,
  Circle,
  Icon
} from '@chakra-ui/react'
import { FC, useMemo } from 'react'
import { FiBarChart2, FiPieChart } from 'react-icons/fi'

import usePurchaseSchedule from '@hook/dashboard/use-purchase-schedule'

import {
  PurchaseScheduleStatus,
  STATUS_CONFIG,
  formatPercentage
} from './utils'

const STATUS_ORDER: PurchaseScheduleStatus[] = [
  'emAtraso',
  'pendente',
  'noPrazo'
]

const STATUS_FILL: Record<PurchaseScheduleStatus, string> = {
  emAtraso: '#E53E3E',
  pendente: '#D69E2E',
  noPrazo: '#38A169'
}

// Ângulo mínimo para que fatias pequenas (mas > 0) continuem visíveis
const MIN_SEGMENT_DEGREES = 4

const StatusBarChart: FC = () => {
  const { statusCounts, statusPercentages } = usePurchaseSchedule()

  return (
    <Box
      bg="white"
      p={5}
      borderRadius="lg"
      border="1px solid"
      borderColor="gray.200"
      shadow="md"
    >
      <HStack mb={4} pb={2} borderBottom="1px solid" borderColor="gray.100">
        <Icon as={FiBarChart2} color="blue.500" />
        <Text fontSize="lg" fontWeight="bold" color="gray.700">
          Distribuição de Status
        </Text>
      </HStack>

      <VStack spacing={5} align="stretch">
        {STATUS_ORDER.map(status => {
          const cfg = STATUS_CONFIG[status]
          const percentage =
            statusCounts.total > 0
              ? (statusCounts[status] / statusCounts.total) * 100
              : 0
          return (
            <VStack key={status} align="stretch" spacing={2}>
              <HStack justify="space-between">
                <HStack spacing={2}>
                  <Icon as={cfg.icon} color={`${cfg.color}.500`} />
                  <Text fontWeight="medium" color="gray.700">
                    {cfg.label}
                  </Text>
                </HStack>
                <Badge
                  colorScheme={cfg.color}
                  fontSize="md"
                  px={3}
                  py={1}
                  borderRadius="full"
                >
                  {statusCounts[status]}
                </Badge>
              </HStack>
              <Progress
                value={percentage}
                colorScheme={cfg.color}
                size="md"
                borderRadius="full"
                hasStripe
                aria-label={`${cfg.label}: ${formatPercentage(
                  statusPercentages[status]
                )}`}
              />
              <Text fontSize="xs" color="gray.500" textAlign="right">
                {formatPercentage(statusPercentages[status])}
              </Text>
            </VStack>
          )
        })}
      </VStack>
    </Box>
  )
}

const StatusDonutChart: FC = () => {
  const { statusPercentages, statusCounts } = usePurchaseSchedule()

  const centerX = 75
  const centerY = 75
  const radius = 65
  const innerRadius = 35
  // Anel desenhado com stroke: raio médio e espessura entre os dois raios
  const ringRadius = (radius + innerRadius) / 2
  const ringWidth = radius - innerRadius
  const circumference = 2 * Math.PI * ringRadius

  // Ângulos calculados pelas contagens brutas (não pelos % arredondados)
  const segments = useMemo(() => {
    const total = statusCounts.total
    if (!total) return []

    const visible = STATUS_ORDER.filter(status => statusCounts[status] > 0).map(
      status => ({
        status,
        degrees: (statusCounts[status] / total) * 360
      })
    )

    if (visible.length > 1) {
      let deficit = 0
      visible.forEach(segment => {
        if (segment.degrees < MIN_SEGMENT_DEGREES) {
          deficit += MIN_SEGMENT_DEGREES - segment.degrees
          segment.degrees = MIN_SEGMENT_DEGREES
        }
      })
      if (deficit > 0) {
        const largest = visible.reduce((a, b) =>
          b.degrees > a.degrees ? b : a
        )
        largest.degrees -= deficit
      }
    }

    let start = 0
    return visible.map(segment => {
      const result = { ...segment, start }
      start += segment.degrees
      return result
    })
  }, [statusCounts])

  return (
    <Box
      bg="white"
      p={5}
      borderRadius="lg"
      border="1px solid"
      borderColor="gray.200"
      shadow="md"
    >
      <HStack mb={4} pb={2} borderBottom="1px solid" borderColor="gray.100">
        <Icon as={FiPieChart} color="blue.500" />
        <Text fontSize="lg" fontWeight="bold" color="gray.700">
          Percentual de Status
        </Text>
      </HStack>

      <VStack spacing={5}>
        {/* Gráfico Donut SVG */}
        <Box
          position="relative"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <svg
            width="150"
            height="150"
            viewBox="0 0 150 150"
            role="img"
            aria-label={STATUS_ORDER.map(
              status =>
                `${STATUS_CONFIG[status].label}: ${formatPercentage(
                  statusPercentages[status]
                )}`
            ).join(', ')}
          >
            {/* Trilho de fundo */}
            <circle
              cx={centerX}
              cy={centerY}
              r={ringRadius}
              fill="none"
              stroke="#EDF2F7"
              strokeWidth={ringWidth}
            />

            {segments.map(segment => (
              <circle
                key={segment.status}
                cx={centerX}
                cy={centerY}
                r={ringRadius}
                fill="none"
                stroke={STATUS_FILL[segment.status]}
                strokeWidth={ringWidth}
                strokeDasharray={`${
                  (segment.degrees / 360) * circumference
                } ${circumference}`}
                transform={`rotate(${
                  segment.start - 90
                } ${centerX} ${centerY})`}
                opacity="0.9"
              />
            ))}

            {/* Texto central integrado no SVG */}
            <text
              x={centerX}
              y={centerY - 6}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize="24"
              fontWeight="bold"
              fill="#1F2937"
            >
              {statusCounts?.total}
            </text>
            <text
              x={centerX}
              y={centerY + 14}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize="12"
              fill="#6B7280"
            >
              Total
            </text>
          </svg>
        </Box>

        <SimpleGrid columns={1} spacing={3} w="full">
          {STATUS_ORDER.map(status => {
            const cfg = STATUS_CONFIG[status]
            const hasValue = statusCounts[status] > 0
            return (
              <HStack
                key={status}
                justify="space-between"
                p={3}
                bg={hasValue ? `${cfg.color}.50` : 'gray.50'}
                borderRadius="md"
                border="1px solid"
                borderColor={hasValue ? `${cfg.color}.200` : 'gray.200'}
              >
                <HStack>
                  <Circle size="16px" bg={`${cfg.color}.500`} />
                  <Text fontSize="sm" fontWeight="medium">
                    {cfg.label}
                  </Text>
                </HStack>
                <HStack>
                  <Text
                    fontWeight="bold"
                    color={`${cfg.color}.600`}
                    fontSize="lg"
                  >
                    {formatPercentage(statusPercentages[status])}
                  </Text>
                  <Badge
                    colorScheme={cfg.color}
                    variant="solid"
                    fontSize="xs"
                    px={2}
                  >
                    {statusCounts[status]}
                  </Badge>
                </HStack>
              </HStack>
            )
          })}
        </SimpleGrid>
      </VStack>
    </Box>
  )
}

const StatusCharts: FC = () => {
  return (
    <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
      <StatusBarChart />
      <StatusDonutChart />
    </SimpleGrid>
  )
}

export default StatusCharts
